import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { submissions as submissionsApi } from '../services/api.js';
import { getRiskTier, getRiskBadgeClass, getRiskLabel } from '../utils/riskUtils';
import './ReviewSummary.css';

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ReviewSummary() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    async function loadReviews() {
      setLoading(true);
      setError(null);
      try {
        const result = await submissionsApi.list();
        const raw = result.data || result;
        const list = raw.data || raw.items || raw || [];
        const allItems = Array.isArray(list) ? list : [];
        const reviewed = allItems.filter((s) => {
          const status = s.Status || s.status || '';
          return status !== 'new' && status !== 'data_extracted';
        });
        setItems(reviewed);
      } catch (err) {
        setError(err.message || 'Failed to load reviews');
      } finally {
        setLoading(false);
      }
    }
    loadReviews();
  }, []);

  const stats = useMemo(() => {
    const total = items.length;
    let bind = 0, decline = 0, refer = 0, scoreSum = 0, scored = 0;
    for (const s of items) {
      const decision = (s.Decision || s.decision || '').toLowerCase();
      if (decision === 'bind') bind++;
      else if (decision === 'decline') decline++;
      else if (decision === 'refer') refer++;
      const score = s.RiskScore || s.riskScore;
      if (score != null && score > 0) {
        scoreSum += score;
        scored++;
      }
    }
    return {
      total,
      bind,
      decline,
      refer,
      avgScore: scored > 0 ? Math.round(scoreSum / scored) : 0,
    };
  }, [items]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'score':
          va = a.RiskScore || a.riskScore || 0;
          vb = b.RiskScore || b.riskScore || 0;
          break;
        case 'decision':
          va = (a.Decision || a.decision || '').toLowerCase();
          vb = (b.Decision || b.decision || '').toLowerCase();
          break;
        case 'date':
          va = a.UpdatedAt || a.updatedAt || a.CreatedAt || a.createdAt || '';
          vb = b.UpdatedAt || b.updatedAt || b.CreatedAt || b.createdAt || '';
          break;
        case 'company':
          va = (a.InsuredName || a.insuredName || '').toLowerCase();
          vb = (b.InsuredName || b.insuredName || '').toLowerCase();
          break;
        case 'revenue':
          va = a.Revenue || a.revenue || 0;
          vb = b.Revenue || b.revenue || 0;
          break;
        default:
          va = 0;
          vb = 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [items, sortField, sortDir]);

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  if (loading) {
    return (
      <div className="rs__loading">
        <div className="spinner" />
        <p>Loading review summary...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rs__error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="rs">
      <h1 className="rs__title">Review Summary</h1>

      {/* Stats bar */}
      <div className="rs__stats">
        <div className="rs__stat">
          <span className="rs__stat-value">{stats.total}</span>
          <span className="rs__stat-label">Total Reviewed</span>
        </div>
        <div className="rs__stat rs__stat--bind">
          <span className="rs__stat-value">{stats.bind}</span>
          <span className="rs__stat-label">Bind</span>
        </div>
        <div className="rs__stat rs__stat--refer">
          <span className="rs__stat-value">{stats.refer}</span>
          <span className="rs__stat-label">Refer</span>
        </div>
        <div className="rs__stat rs__stat--decline">
          <span className="rs__stat-value">{stats.decline}</span>
          <span className="rs__stat-label">Decline</span>
        </div>
        <div className="rs__stat">
          <span className="rs__stat-value">{stats.avgScore || '—'}</span>
          <span className="rs__stat-label">Avg Score</span>
        </div>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <p className="rs__empty">No reviewed submissions yet.</p>
      ) : (
        <div className="rs__table-wrap">
          <table className="rs__table">
            <thead>
              <tr>
                <th className="rs__th rs__th--sortable" onClick={() => toggleSort('company')}>
                  Company{sortIndicator('company')}
                </th>
                <th className="rs__th">Industry</th>
                <th className="rs__th rs__th--sortable" onClick={() => toggleSort('revenue')}>
                  Revenue{sortIndicator('revenue')}
                </th>
                <th className="rs__th rs__th--sortable" onClick={() => toggleSort('score')}>
                  Score{sortIndicator('score')}
                </th>
                <th className="rs__th">Tier</th>
                <th className="rs__th rs__th--sortable" onClick={() => toggleSort('decision')}>
                  Decision{sortIndicator('decision')}
                </th>
                <th className="rs__th rs__th--sortable" onClick={() => toggleSort('date')}>
                  Date{sortIndicator('date')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((s) => {
                const id = s.SubmissionID || s.submissionId || s.id;
                const companyName = s.InsuredName || s.insuredName || s.CompanyName || s.companyName || '—';
                const industry = s.Industry || s.industry || '—';
                const revenue = s.Revenue || s.revenue;
                const score = s.RiskScore || s.riskScore;
                const tier = s.RiskTier || s.riskTier || (score != null ? getRiskTier(score) : null);
                const decision = s.Decision || s.decision || '';
                const date = s.UpdatedAt || s.updatedAt || s.CreatedAt || s.createdAt;

                return (
                  <tr
                    key={id}
                    className="rs__row"
                    onClick={() => navigate(`/underwriting/${id}`)}
                  >
                    <td className="rs__td rs__td--company">{companyName}</td>
                    <td className="rs__td">{industry}</td>
                    <td className="rs__td">{formatCurrency(revenue)}</td>
                    <td className="rs__td rs__td--score">{score ?? '—'}</td>
                    <td className="rs__td">
                      {tier ? (
                        <span className={getRiskBadgeClass(score)}>
                          {getRiskLabel(tier)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="rs__td">
                      {decision ? (
                        <span className={`rs__decision-badge rs__decision-badge--${decision.toLowerCase()}`}>
                          {decision}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="rs__td rs__td--date">{formatDate(date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
